import type {
  AnalyticsInitConfig,
  ListenerEntry,
  PreInitBehavior,
  RemoteListenerEntry,
} from './types/config.js';
import type { AnalyticsListener } from './types/listener.js';

const VALID_PRE_INIT_BEHAVIORS: PreInitBehavior[] = ['drop', 'queue'];

function isRemoteListenerEntry(entry: unknown): entry is RemoteListenerEntry {
  if (entry == null || typeof entry !== 'object') return false;
  const o = entry as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    o.id.trim() !== '' &&
    'config' in o &&
    !('listener' in o && o.listener != null)
  );
}

/**
 * Validate init config. Throws with clear message on invalid config.
 * appId and environment are always taken from init options (e.g. from env files); when configUrl/getConfig is set, only listener details come from the remote source.
 */
export function validateConfig(options: AnalyticsInitConfig): void {
  const useRemoteConfig = Boolean(options.configUrl ?? options.getConfig);
  if (typeof options.appId !== 'string' || options.appId.trim() === '') {
    throw new Error('Analytics init failed: appId is required and must be a non-empty string (e.g. from environment variables).');
  }
  if (options.listeners != null && !Array.isArray(options.listeners)) {
    throw new Error('Analytics init failed: listeners must be an array.');
  }
  const behavior = options.preInitBehavior ?? 'drop';
  if (!VALID_PRE_INIT_BEHAVIORS.includes(behavior)) {
    throw new Error(
      `Analytics init failed: preInitBehavior must be 'queue' or 'drop', got '${behavior}'.`
    );
  }
  if (behavior === 'queue') {
    const maxSize = options.queueMaxSize ?? 100;
    const ttlMs = options.queueTtlMs ?? 5000;
    if (typeof maxSize !== 'number' || maxSize <= 0) {
      throw new Error('Analytics init failed: queueMaxSize must be a positive number.');
    }
    if (typeof ttlMs !== 'number' || ttlMs <= 0) {
      throw new Error('Analytics init failed: queueTtlMs must be a positive number.');
    }
  }
  if (useRemoteConfig) {
    return;
  }
  const listeners = options.listeners ?? [];
  const isRemoteShape = listeners.length > 0 && listeners.every(isRemoteListenerEntry);
  if (isRemoteShape) {
    for (let i = 0; i < listeners.length; i++) {
      const entry = listeners[i] as RemoteListenerEntry;
      if (entry.enabled === false) continue;
      if (typeof entry.id !== 'string' || entry.id.trim() === '') {
        throw new Error(`Analytics init failed: listeners[${i}].id is required and must be a non-empty string.`);
      }
    }
    return;
  }
  for (let i = 0; i < listeners.length; i++) {
    const entry = listeners[i] as ListenerEntry;
    if (entry.enabled === false) continue;
    if (entry.listener == null || typeof entry.listener !== 'object') {
      throw new Error(`Analytics init failed: listeners[${i}].listener is required.`);
    }
    const listener = entry.listener as AnalyticsListener;
    if (typeof listener.track !== 'function') {
      throw new Error(`Analytics init failed: listeners[${i}].listener must have a track method.`);
    }
  }
}
